import React, {useContext, useEffect, useMemo, useRef, useState} from 'react'
import AntdButton from 'components/atom/Buttons/AntdButton'
import {useSelector} from 'react-redux'
import {RootState} from 'redux/store'
import {Tooltip} from 'antd'
import {AllTreatmentPlanListItem} from 'screens/Patients/LeadsProfile/main/treatment/types/treatmentPlan.types'
import {useManufacturingDetails} from 'screens/Patients/LeadsProfile/main/overview/hooks/useManufacturingDetails'
import {ManufacturingDetailsCard} from 'screens/Patients/LeadsProfile/main/overview/components/ManufacturingDetailsCard'
import {safeParseInt} from 'utils/ConstFunctions'
import {BatchList} from '../components/BatchList'
import {getManufacturingListDetails} from 'redux/Slices/AppSlice/LeadsProfile/GettingStartedOverview/GettingStartedOverview.slice'
import {useNavigate, useParams, useSearchParams, useLocation} from 'react-router-dom'
import useDispatchAction from '@hooks/useDispatchAction'
import hasValue from 'utils/hasValue'
import BorderedCardForDashBoardCards from 'screens/Dashboard/components/BorderedCard'
import {getAllTreatmentPlanList} from 'redux/Slices/AppSlice/LeadsProfile/LeadsProfileTreatmentPlan.slice'
import {getNewTreatmentList} from 'redux/Slices/AppSlice/Kanban/Kanban.slice'
import {AuthContext} from 'context/AuthContext'
import treatmentPlanStatusConstants from '@constants/treatmentPlanStatus.constants'
import InfoCard from 'components/instruction/InfoCard'
import getColorPalette from 'utils/getColorPalette'
import moment from 'moment'
import When from 'components/when/When'
import StartManufacturingModal from 'screens/Patients/LeadsProfile/main/overview/components/StartManufacturingModal'
import CheckedCircleIcon from 'assets/icons/CheckedCircleIcon'
import {useFeatureAccess} from '@hooks/useFeatureAccess'
import PlanStatusTag from '../helpers/PlanStatusTag'
import {getPlanStatus} from '@utils/getPlanStatus'
import TabHeader from '../items/TabHeader'
import useAllUserPlan from '@hooks/useAllUserPlan'
import Spinner from 'components/spinner/Spinner'
import {useTreatmentPlanFilter} from '@hooks/useTreatmentPlanFilter'
import {NewPlanData} from '../types/PlanList.types'

export type TreatmentPlan = {
  id: string
  title: string
  version?: string
  cards: TreatmentPlanCardData[]
}

export type TreatmentPlanCardData = {
  label: string
  value: string
  lines?: string[]
  note?: string
}

type DeliveredTotals = {
  totalAligners: number
  upperJaw: {start: number | null; end: number | null}
  lowerJaw: {start: number | null; end: number | null}
}

const parseAlignerSeries = (series?: string | null) => {
  const matches = String(series ?? '').match(/\d+/g) ?? []
  const start = safeParseInt(matches[0])
  const end = safeParseInt(matches[1] ?? matches[0])

  if (!start || !end || end < start) {
    return {
      starts_with: 0,
      ends_with: 0,
      range: [],
    }
  }

  return {
    starts_with: start,
    ends_with: end,
    range: Array.from({length: end - start + 1}, (_, index) => start + index),
  }
}

const getRangeCount = ({starts_with, ends_with}: {starts_with: number; ends_with: number}) => {
  if (!starts_with || !ends_with || ends_with < starts_with) return 0
  return ends_with - starts_with + 1
}

const normalizeWorkflowPlan = (plan: NewPlanData): AllTreatmentPlanListItem => {
  const upperJaw = parseAlignerSeries(plan.upper_aligner_series)
  const lowerJaw = parseAlignerSeries(plan.lower_aligner_series)

  return {
    aligner_treatment_id: safeParseInt(plan.plan_id),
    braces_treatment_created_on: null,
    braces_treatment_id: null,
    total_aligner: getRangeCount(upperJaw) + getRangeCount(lowerJaw),
    treatment_name: plan.treatment_plan_name ?? 'Treatment Plan',
    created_by_me: false,
    planning_link: plan.planning_link ?? '',
    treatment_status: plan.status,
    treatment_type: 'ALIGNERS',
    order_id: plan.order_id,
    created_at: plan.created_date ?? plan.upload_date ?? '',
    treatment_planning_link: plan.planning_link ?? null,
    doctor_added_treatment_name: plan.treatment_plan_name ?? '',
    treatment_plan_tag_name: plan.treatment_plan_tag_name,
    approved_by_patient_at: plan.approved_date,
    approved_by_patient: false,
    braces_notes_attached: false,
    upper_jaw_details: upperJaw,
    lower_jaw_details: lowerJaw,
    initiator_status: plan.initiator_status,
    approver_status: plan.approver_status,
    treatment_plan_metadata: plan.treatment_plan_metadata
      ? {
          replan_reason: plan.treatment_plan_metadata.replan_reason ?? null,
          replan_requested_on: plan.treatment_plan_metadata.replan_requested_on,
        }
      : undefined,
  }
}

const getTreatmentPlanIdentity = (plan: AllTreatmentPlanListItem) => {
  const treatmentPlanId = safeParseInt(plan?.aligner_treatment_id)

  if (treatmentPlanId > 0) {
    return `aligner-${treatmentPlanId}`
  }

  return [
    plan?.order_id ?? 'no-order',
    plan?.treatment_type ?? 'no-type',
    plan?.treatment_plan_tag_name ?? plan?.treatment_name ?? plan?.doctor_added_treatment_name,
    plan?.created_at ?? 'no-created-at',
  ].join('|')
}

const getUniqueTreatmentPlans = (plans: AllTreatmentPlanListItem[]) => {
  const seenPlanKeys = new Set<string>()

  return plans.filter((plan) => {
    const planKey = getTreatmentPlanIdentity(plan)

    if (seenPlanKeys.has(planKey)) return false

    seenPlanKeys.add(planKey)
    return true
  })
}

// Helper function to check if a batch has valid data
const hasValidBatchData = (batch: any): boolean => {
  const hasUpperData =
    batch.upperJaw?.start !== null &&
    batch.upperJaw?.start !== undefined &&
    batch.upperJaw?.end !== null &&
    batch.upperJaw?.end !== undefined
  const hasLowerData =
    batch.lowerJaw?.start !== null &&
    batch.lowerJaw?.start !== undefined &&
    batch.lowerJaw?.end !== null &&
    batch.lowerJaw?.end !== undefined
  const hasTotalAligners = (batch.totalAligners ?? 0) > 0
  return hasUpperData || hasLowerData || hasTotalAligners
}

const TreatmentPlanSection: React.FC<{
  plan: AllTreatmentPlanListItem
  setIsDisabled: React.Dispatch<React.SetStateAction<boolean>>
  isViewOnly?: boolean
}> = ({plan, setIsDisabled, isViewOnly = false}) => {
  // ========== ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS ==========
  const navigate = useNavigate()
  const {profileId} = useContext(AuthContext)
  const {isStarterPlanUser, isEnterprisePlanUser, isPractice} = useAllUserPlan()
  const {isPastPlan, isLatestPlan} = useTreatmentPlanFilter()
  const {dispatchAction} = useDispatchAction()
  const {patientId} = useParams<{patientId: string}>()
  const [searchParams] = useSearchParams()
  const orderIdFromUrl = searchParams.get('order_id')
  const treatmentIdFromUrl =
    searchParams.get('treatment_plan_id') ?? searchParams.get('treatmentPlanId')

  // Get patientType from URL
  const patientType = searchParams.get('patientType') || 'EXISTING_PATIENT'

  // ALL useSelector hooks must be at the top
  const {getIndividualTaskList} = useSelector((state: RootState) => state.workFlow)
  const {openModalManufacturing} = useSelector((state: RootState) => state.GettingStartedOverview)
  const {serviceConfig} = useSelector((state: RootState) => state.serviceConfiguration)
  const {permissionChecks} = useFeatureAccess()

  // Get openModalManufacturing again for the StartManufacturingModal
  const openModalManufacturingState = useSelector(
    (state: RootState) => state.GettingStartedOverview.openModalManufacturing
  )

  const {selectedOrderId} = useSelector((state: RootState) => state.customerPatientProfile)
  const {patientOrderList} = useSelector((state: RootState) => state.profile)

  // Determine if this is the latest order
  const latestOrderId = useMemo(() => {
    const orders = (patientOrderList ?? [])
      .filter((order) => Boolean(order.order_id) && order.case_submitted === true)
      .sort(
        (a, b) =>
          new Date(a.order_creation_date ?? '').getTime() -
          new Date(b.order_creation_date ?? '').getTime()
      )
    return orders.length > 0 ? String(orders[orders.length - 1].order_id) : null
  }, [patientOrderList])

  const currentOrderId = orderIdFromUrl ?? selectedOrderId
  const isLatestOrder = latestOrderId ? currentOrderId === latestOrderId : true
  const isPreviousOrder = !isLatestOrder && orderIdFromUrl !== null

  // ALWAYS call useManufacturingDetails - never conditionally
  const {processed, latest_manufacturing_data, unprocessed, loading} = useManufacturingDetails({
    treatment_plan_id: plan.aligner_treatment_id,
    getFreshData: true,
  })

  // All useMemo hooks must be called before any return
  const processed_reverse = useMemo(() => {
    return [...(processed ?? [])]
  }, [processed])

  const inProgress = ['MANUFACTURING_STARTED', 'COMPLETED', 'IN_PROGRESS', 'SHIPPED'].includes(
    latest_manufacturing_data?.status ?? ''
  )

  const unprocessedTotal = safeParseInt(unprocessed?.total_aligners) ?? 0
  const hasUnprocessed = (unprocessedTotal as number) > 0

  const status = plan?.treatment_status
  const isActiveOrApproved = useMemo(
    () =>
      [treatmentPlanStatusConstants.ACTIVE, treatmentPlanStatusConstants.APPROVED].includes(
        status as any
      ),
    [status]
  )
  const isDeactivated = status === treatmentPlanStatusConstants.DEACTIVATED

  const beginNextBatchAccess =
    permissionChecks?.patientProfileActions?.beginNextBatch?.isViewable &&
    serviceConfig?.ALIGNER_PLANNING_MANUFACTURING

  const dueByDate = unprocessed?.due_by ? moment(unprocessed?.due_by).format('DD-MMM-YYYY') : null

  const deliveredBatches = processed_reverse.filter((b) => b.status === 'DELIVERED')

  const deliveredTotals = deliveredBatches.reduce<DeliveredTotals>(
    (acc, batch) => {
      acc.totalAligners += Number(batch.totalAligners || 0)

      const updateRange = (
        accObj: {start: number | null; end: number | null},
        curr: {start?: number | null; end?: number | null}
      ) => {
        const s = curr?.start ?? null
        const e = curr?.end ?? null
        if (s !== null && s !== undefined) {
          accObj.start = accObj.start === null ? s : Math.min(accObj.start, s)
        }
        if (e !== null && e !== undefined) {
          accObj.end = accObj.end === null ? e : Math.max(accObj.end, e)
        }
      }

      updateRange(acc.upperJaw, batch.upperJaw || {})
      updateRange(acc.lowerJaw, batch.lowerJaw || {})

      return acc
    },
    {
      totalAligners: 0,
      upperJaw: {start: null, end: null},
      lowerJaw: {start: null, end: null},
    }
  )

  const nextUpperStart = safeParseInt(unprocessed?.upperJaw?.start)
  const nextUpperEndMax = safeParseInt(unprocessed?.upperJaw?.end)
  const nextLowerStart = safeParseInt(unprocessed?.lowerJaw?.start)
  const nextLowerEndMax = safeParseInt(unprocessed?.lowerJaw?.end)
  const stepSize = 5
  const nextUpperEnd =
    Number.isFinite(nextUpperStart) && Number.isFinite(nextUpperEndMax)
      ? Math.min((nextUpperStart as number) + (stepSize - 1), nextUpperEndMax as number)
      : null
  const nextLowerEnd =
    Number.isFinite(nextLowerStart) && Number.isFinite(nextLowerEndMax)
      ? Math.min((nextLowerStart as number) + (stepSize - 1), nextLowerEndMax as number)
      : null
  const count = (s?: number | null, e?: number | null) =>
    Number.isFinite(s) && Number.isFinite(e) && (e as number) >= (s as number)
      ? (e as number) - (s as number) + 1
      : 0
  const nextTotal = count(nextUpperStart, nextUpperEnd) + count(nextLowerStart, nextLowerEnd)

  const isPrevOutsourced = useMemo(() => {
    const profId = (latest_manufacturing_data as any)?.service_products?.profile_id
    return Number(profileId) !== Number(profId)
  }, [latest_manufacturing_data, profileId])

  const treatmentStatus = getPlanStatus({
    treatmentPlan: plan as any,
  }) as keyof typeof treatmentPlanStatusConstants

  // Past refinement plans are read-only for Starter, Enterprise, and Growth flows.
  const isDisabledForPastPlan = isPastPlan
  // Check if this is a previous order for enterprise
  const isDisabledForPreviousOrder = isPreviousOrder
  // Check if view-only mode is enabled (for enterprise existing patients viewing previous plans)
  const isViewOnlyMode =
    isViewOnly ||
    ((isEnterprisePlanUser || isPractice) &&
      patientType === 'EXISTING_PATIENT' &&
      treatmentIdFromUrl &&
      !isLatestPlan)

  // ========== FIX: Calculate unprocessed data correctly with proper range ==========
  const unprocessedData = useMemo(() => {
    // For view-only, past plans, or previous orders, calculate from plan data
    if (isViewOnlyMode || isDisabledForPastPlan || isDisabledForPreviousOrder) {
      const totalAligners = plan?.total_aligner || 0
      const deliveredCount = deliveredTotals.totalAligners || 0
      const unprocessedCount = Math.max(0, totalAligners - deliveredCount)

      // Get the original ranges from the plan
      let upperStart = plan?.upper_jaw_details?.starts_with
      let upperEnd = plan?.upper_jaw_details?.ends_with
      let lowerStart = plan?.lower_jaw_details?.starts_with
      let lowerEnd = plan?.lower_jaw_details?.ends_with

      // Adjust the range to start after delivered aligners
      // For upper jaw
      if (upperStart && upperEnd) {
        const startNum = Number(upperStart)
        const endNum = Number(upperEnd)
        // Calculate how many upper aligners were delivered
        let deliveredUpper = 0
        deliveredBatches.forEach((batch) => {
          if (batch.upperJaw?.start && batch.upperJaw?.end) {
            const s = Number(batch.upperJaw.start)
            const e = Number(batch.upperJaw.end)
            // Count delivered aligners that fall within the range
            const overlapStart = Math.max(s, startNum)
            const overlapEnd = Math.min(e, endNum)
            if (overlapStart <= overlapEnd) {
              deliveredUpper += overlapEnd - overlapStart + 1
            }
          }
        })

        const remainingUpper = Math.max(0, endNum - startNum + 1 - deliveredUpper)
        if (remainingUpper > 0) {
          upperStart = startNum + deliveredUpper
          // upperEnd stays the same
        } else {
          upperStart = null
          upperEnd = null
        }
      }

      // For lower jaw
      if (lowerStart && lowerEnd) {
        const startNum = Number(lowerStart)
        const endNum = Number(lowerEnd)
        let deliveredLower = 0
        deliveredBatches.forEach((batch) => {
          if (batch.lowerJaw?.start && batch.lowerJaw?.end) {
            const s = Number(batch.lowerJaw.start)
            const e = Number(batch.lowerJaw.end)
            const overlapStart = Math.max(s, startNum)
            const overlapEnd = Math.min(e, endNum)
            if (overlapStart <= overlapEnd) {
              deliveredLower += overlapEnd - overlapStart + 1
            }
          }
        })

        const remainingLower = Math.max(0, endNum - startNum + 1 - deliveredLower)
        if (remainingLower > 0) {
          lowerStart = startNum + deliveredLower
          // lowerEnd stays the same
        } else {
          lowerStart = null
          lowerEnd = null
        }
      }

      return {
        totalAligners: unprocessedCount,
        upperJaw: {
          start: upperStart,
          end: upperEnd,
        },
        lowerJaw: {
          start: lowerStart,
          end: lowerEnd,
        },
      }
    }

    // For active plans, use the API data
    return {
      totalAligners: safeParseInt(unprocessed?.total_aligners),
      upperJaw: {
        start: safeParseInt(unprocessed?.upperJaw?.start),
        end: safeParseInt(unprocessed?.upperJaw?.end),
      },
      lowerJaw: {
        start: safeParseInt(unprocessed?.lowerJaw?.start),
        end: safeParseInt(unprocessed?.lowerJaw?.end),
      },
    }
  }, [
    isViewOnlyMode,
    isDisabledForPastPlan,
    isDisabledForPreviousOrder,
    plan,
    deliveredTotals,
    deliveredBatches,
    unprocessed,
  ])

  // All useEffect hooks must be called before any return
  useEffect(() => {
    setIsDisabled(
      !(hasUnprocessed && !inProgress) ||
        isDeactivated ||
        isDisabledForPastPlan ||
        isDisabledForPreviousOrder ||
        isViewOnlyMode
    )
  }, [
    hasUnprocessed,
    inProgress,
    status,
    setIsDisabled,
    isDisabledForPastPlan,
    isDisabledForPreviousOrder,
    isViewOnlyMode,
  ])

  const refreshData = () => {
    if (!hasValue(plan?.aligner_treatment_id) && plan?.aligner_treatment_id === 0) return
    dispatchAction(
      getManufacturingListDetails({
        patient_id: safeParseInt(patientId),
        treatment_plan_id: safeParseInt(plan?.aligner_treatment_id),
      })
    ).unwrap()
  }

  useEffect(() => {
    if (openModalManufacturing) refreshData()
  }, [openModalManufacturing])

  // ========== DEBUG LOGS ==========

  if (loading) {
    return <Spinner loading />
  }

  // Show disabled state for view-only mode, past plans, or previous orders
  if (isViewOnlyMode || isDisabledForPastPlan || isDisabledForPreviousOrder) {
    return (
      <section className='mb-8'>
        <BorderedCardForDashBoardCards>
          <div className='flex items-center gap-2'>
            <h1 className='text-lg md:text-xl font-semibold text-gray-900'>
              {plan?.treatment_plan_tag_name ?? plan?.treatment_name}
            </h1>
            <h1 className='text-base md:text-base text-textColor'>
              {plan?.treatment_version || unprocessed?.treatment_version || 'V1'}
            </h1>
            <CheckedCircleIcon color={getColorPalette().tertiaryColor} />
            <PlanStatusTag treatmentStatus={treatmentStatus} />
          </div>

          <div className='md:flex-row flex flex-col gap-2 justify-between'>
            {/* TOTAL ALIGNER - Always use plan data */}
            <ManufacturingDetailsCard
              totalAligners={plan?.total_aligner}
              upperJaw={{
                start: plan?.upper_jaw_details?.starts_with,
                end: plan?.upper_jaw_details?.ends_with,
              }}
              lowerJaw={{
                start: plan?.lower_jaw_details?.starts_with,
                end: plan?.lower_jaw_details?.ends_with,
              }}
              title={'TOTAL ALIGNER'}
            />

            {/* UNPROCESSED - FIXED: Use calculated unprocessed data */}
            <ManufacturingDetailsCard
              totalAligners={unprocessedData.totalAligners}
              upperJaw={unprocessedData.upperJaw}
              lowerJaw={unprocessedData.lowerJaw}
              title={'UNPROCESSED'}
            />

            <ManufacturingDetailsCard
              title='IN MANUFACTURING'
              status={latest_manufacturing_data?.status}
              totalAligners={
                ['MANUFACTURING_STARTED', 'COMPLETED', 'IN_PROGRESS', 'SHIPPED'].includes(
                  latest_manufacturing_data?.status ?? ''
                )
                  ? latest_manufacturing_data?.total_aligners
                  : 0
              }
              upperJaw={{
                start: ['MANUFACTURING_STARTED', 'COMPLETED', 'IN_PROGRESS', 'SHIPPED'].includes(
                  latest_manufacturing_data?.status ?? ''
                )
                  ? safeParseInt(latest_manufacturing_data?.upper_aligner_start)
                  : '',
                end: ['MANUFACTURING_STARTED', 'COMPLETED', 'IN_PROGRESS', 'SHIPPED'].includes(
                  latest_manufacturing_data?.status ?? ''
                )
                  ? safeParseInt(latest_manufacturing_data?.upper_aligner_end)
                  : '',
              }}
              lowerJaw={{
                start: ['MANUFACTURING_STARTED', 'COMPLETED', 'IN_PROGRESS', 'SHIPPED'].includes(
                  latest_manufacturing_data?.status ?? ''
                )
                  ? safeParseInt(latest_manufacturing_data?.lower_aligner_start)
                  : '',
                end: ['MANUFACTURING_STARTED', 'COMPLETED', 'IN_PROGRESS', 'SHIPPED'].includes(
                  latest_manufacturing_data?.status ?? ''
                )
                  ? safeParseInt(latest_manufacturing_data?.lower_aligner_end)
                  : '',
              }}
            />

            <ManufacturingDetailsCard
              title='DELIVERED'
              status={deliveredTotals.totalAligners > 0 ? 'DELIVERED' : undefined}
              totalAligners={deliveredTotals.totalAligners}
              upperJaw={{
                start: deliveredTotals.upperJaw.start ?? '',
                end: deliveredTotals.upperJaw.end ?? '',
              }}
              lowerJaw={{
                start: deliveredTotals.lowerJaw.start ?? '',
                end: deliveredTotals.lowerJaw.end ?? '',
              }}
            />
          </div>

          {!isStarterPlanUser &&
            processed_reverse
              .filter(hasValidBatchData)
              .map((batch, idx) => (
                <BatchList
                  readOnly
                  key={batch.id ?? idx}
                  processed={batch}
                  index={idx}
                  plan={plan}
                  totalBatches={processed_reverse.length}
                  latestManufacturingData={latest_manufacturing_data}
                />
              ))}
        </BorderedCardForDashBoardCards>
      </section>
    )
  }

  // Normal render for active/latest plans
  return (
    <section className='mb-8'>
      <BorderedCardForDashBoardCards>
        <div className='flex items-center gap-2'>
          <h1 className='text-lg md:text-xl font-semibold text-gray-900'>
            {plan?.treatment_plan_tag_name ?? plan?.treatment_name}
          </h1>
          <h1 className='text-base md:text-base text-textColor'>
            {unprocessed?.treatment_version}
          </h1>
          <CheckedCircleIcon color={getColorPalette().tertiaryColor} />
          <PlanStatusTag treatmentStatus={treatmentStatus} />
        </div>

        <When isTrue={!loading}>
          <div className='md:flex-row flex flex-col gap-2 justify-between'>
            <ManufacturingDetailsCard
              totalAligners={plan?.total_aligner}
              upperJaw={{
                start: plan?.upper_jaw_details?.starts_with,
                end: plan?.upper_jaw_details?.ends_with,
              }}
              lowerJaw={{
                start: plan?.lower_jaw_details?.starts_with,
                end: plan?.lower_jaw_details?.ends_with,
              }}
              title={'TOTAL ALIGNER'}
            />

            <ManufacturingDetailsCard
              totalAligners={unprocessedData.totalAligners}
              upperJaw={unprocessedData.upperJaw}
              lowerJaw={unprocessedData.lowerJaw}
              title={'UNPROCESSED'}
            >
              <When
                isTrue={
                  (isActiveOrApproved &&
                    (unprocessedTotal as number) > 0 &&
                    !inProgress &&
                    beginNextBatchAccess) ||
                  (isStarterPlanUser && (unprocessedTotal as number) > 0)
                }
              >
                <AntdButton
                  className='mt-2 text-white bg-primaryColor border border-primaryColor font-semibold w-fit px-3 py-1.5 rounded-lg'
                  text={`${isStarterPlanUser ? 'Record new batch' : '+ Begin next batch'}`}
                  onClick={() => {
                    if (!!patientId && !!plan?.aligner_treatment_id) {
                      if (isStarterPlanUser) {
                        navigate(
                          `/starter-plan-production/${patientId}/${plan?.aligner_treatment_id}`
                        )
                        return
                      }

                      const prefill = {
                        from: 'next-batch',
                        prevBatchId:
                          (latest_manufacturing_data as any)?.manufacturing_batch_id ?? null,
                        productType: isPrevOutsourced ? 'OUTSOURCE' : 'IN_HOUSE',
                        productSelected: {
                          id: -1,
                          product_type: 'ALIGNER',
                          product_name:
                            (latest_manufacturing_data as any)?.service_products?.product_name ||
                            (latest_manufacturing_data as any)?.service_products?.product_type ||
                            'Aligner',
                          product_description: '',
                          product_image: null,
                          product_category_id: 0,
                          product_category_name:
                            (latest_manufacturing_data as any)?.service_products
                              ?.product_category_name || 'Aligners',
                          profile_id:
                            (latest_manufacturing_data as any)?.service_products?.profile_id ||
                            Number(profileId),
                          is_default: false,
                          created_at: '',
                          updated_at: '',
                          is_last_used: true,
                          added_by_user_name: '',
                        },
                        manufacturingData: {
                          batchType: 'IN_BATCHES',
                          upper_start: nextUpperStart ?? null,
                          upper_end: nextUpperEnd ?? null,
                          lower_start: nextLowerStart ?? null,
                          lower_end: nextLowerEnd ?? null,
                          total: nextTotal || safeParseInt(unprocessed?.total_aligners) || null,
                        },
                        readonlyPlan: true,
                      }
                      navigate(
                        `/production-setup-stepper/${patientId}/${plan?.aligner_treatment_id}?prefill=next-batch`,
                        {
                          state: {prefill},
                        }
                      )
                    }
                  }}
                />
              </When>
            </ManufacturingDetailsCard>

            <ManufacturingDetailsCard
              title='IN MANUFACTURING'
              status={latest_manufacturing_data?.status}
              totalAligners={
                ['MANUFACTURING_STARTED', 'COMPLETED', 'IN_PROGRESS', 'SHIPPED'].includes(
                  latest_manufacturing_data?.status ?? ''
                )
                  ? latest_manufacturing_data?.total_aligners
                  : 0
              }
              upperJaw={{
                start: ['MANUFACTURING_STARTED', 'COMPLETED', 'IN_PROGRESS', 'SHIPPED'].includes(
                  latest_manufacturing_data?.status ?? ''
                )
                  ? safeParseInt(latest_manufacturing_data?.upper_aligner_start)
                  : '',
                end: ['MANUFACTURING_STARTED', 'COMPLETED', 'IN_PROGRESS', 'SHIPPED'].includes(
                  latest_manufacturing_data?.status ?? ''
                )
                  ? safeParseInt(latest_manufacturing_data?.upper_aligner_end)
                  : '',
              }}
              lowerJaw={{
                start: ['MANUFACTURING_STARTED', 'COMPLETED', 'IN_PROGRESS', 'SHIPPED'].includes(
                  latest_manufacturing_data?.status ?? ''
                )
                  ? safeParseInt(latest_manufacturing_data?.lower_aligner_start)
                  : '',
                end: ['MANUFACTURING_STARTED', 'COMPLETED', 'IN_PROGRESS', 'SHIPPED'].includes(
                  latest_manufacturing_data?.status ?? ''
                )
                  ? safeParseInt(latest_manufacturing_data?.lower_aligner_end)
                  : '',
              }}
            >
              {/* ========== "View Ongoing Production" CTA ========== */}
              <When
                isTrue={
                  isActiveOrApproved &&
                  ['MANUFACTURING_STARTED', 'COMPLETED', 'IN_PROGRESS', 'SHIPPED'].includes(
                    latest_manufacturing_data?.status ?? ''
                  ) &&
                  (latest_manufacturing_data?.total_aligners ?? 0) > 0
                }
              >
                <Tooltip title='Ongoing Production: Manage active batches currently under production.'>
                  <div className='inline-block'>
                    <AntdButton
                      className='mt-2 text-white bg-primaryColor border border-primaryColor font-semibold w-fit px-3 py-1.5 rounded-lg'
                      text='View Ongoing Production'
                      onClick={() => {
                        const batchId = (latest_manufacturing_data as any)?.manufacturing_batch_id

                        const params = new URLSearchParams()
                        params.set('tab', 'ongoing')
                        if (batchId) params.set('batchId', String(batchId))
                        if (getIndividualTaskList?.patient_name)
                          params.set('patientName', String(getIndividualTaskList?.patient_name))
                        navigate(`/aligner-production?${params.toString()}`)
                      }}
                    />
                  </div>
                </Tooltip>
              </When>
            </ManufacturingDetailsCard>

            <ManufacturingDetailsCard
              title='DELIVERED'
              status={deliveredTotals.totalAligners > 0 ? 'DELIVERED' : undefined}
              totalAligners={deliveredTotals.totalAligners}
              upperJaw={{
                start: deliveredTotals.upperJaw.start ?? '',
                end: deliveredTotals.upperJaw.end ?? '',
              }}
              lowerJaw={{
                start: deliveredTotals.lowerJaw.start ?? '',
                end: deliveredTotals.lowerJaw.end ?? '',
              }}
            />
          </div>
        </When>

        <When isTrue={!!dueByDate && !isStarterPlanUser}>
          <InfoCard
            {...{
              title: `Next Batch Due by ${dueByDate}`,
              showButton: false,
              titleClassName: 'text-base font-semibold text-black',
              className: 'bg-secondarySupport border border-secondaryColor',
              infoIconColor: getColorPalette().secondaryColor,
            }}
          />
        </When>
        <When isTrue={!isStarterPlanUser}>
          {processed_reverse.filter(hasValidBatchData).map((batch, idx) => (
            <BatchList
              key={batch.id ?? idx}
              processed={batch}
              index={idx}
              plan={plan}
              totalBatches={processed_reverse.length}
              latestManufacturingData={latest_manufacturing_data}
            />
          ))}
          <StartManufacturingModal
            openModal={openModalManufacturingState}
            refreshData={refreshData}
            treatment_plan_id={plan?.aligner_treatment_id}
          />
        </When>
      </BorderedCardForDashBoardCards>
    </section>
  )
}

const StandardProduction: React.FC = () => {
  // ========== ALL HOOKS MUST BE AT THE TOP ==========
  const {allTreatmentPlanList, getAllTreatmentPlanListLoading} = useSelector(
    (state: RootState) => state.leadsProfileTreatmentPlanReducer
  )

  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const orderIdFromUrl = searchParams.get('order_id')
  const treatmentIdFromUrl =
    searchParams.get('treatment_plan_id') ?? searchParams.get('treatmentPlanId')
  const patientType = searchParams.get('patientType') || 'EXISTING_PATIENT'

  const [, setIsDisabled] = useState<boolean>(true)
  const requestedTreatmentPlansForPatientRef = useRef<string | null>(null)
  const {userId, organizationId} = useContext(AuthContext)
  const {dispatchAction} = useDispatchAction()
  const {patientId} = useParams<{patientId: string}>()
  const hasTreatmentPlans = !!allTreatmentPlanList?.length

  const {selectedOrderId} = useSelector((state: RootState) => state.customerPatientProfile)
  const {patientOrderList} = useSelector((state: RootState) => state.profile)
  const {data} = useSelector((state: RootState) => state.apiGetLeadsProfileDetails)
  const {plansList} = useSelector((state: RootState) => state.kanban)
  const {isStarterPlanUser, isPastPlan, latestPlanId} = useTreatmentPlanFilter()
  const {isEnterprisePlanUser, isPractice, isCustomer, isGrowthPlanUser} = useAllUserPlan()

  const currentTreatmentIdRef = useRef<string | null>(treatmentIdFromUrl)
  const currentOrderIdRef = useRef<string | null>(orderIdFromUrl)

  const latestOrderId = useMemo(() => {
    const orders = (patientOrderList ?? [])
      .filter((order) => Boolean(order.order_id) && order.case_submitted === true)
      .sort(
        (a, b) =>
          new Date(a.order_creation_date ?? '').getTime() -
          new Date(b.order_creation_date ?? '').getTime()
      )
    return orders.length > 0 ? String(orders[orders.length - 1].order_id) : null
  }, [patientOrderList])

  const currentOrderId = orderIdFromUrl ?? selectedOrderId
  const isLatestOrder = latestOrderId ? currentOrderId === latestOrderId : true
  const isExistingPatient =
    (data?.patient_details?.patient_type ?? patientType) === 'EXISTING_PATIENT'
  const isGrowthOrderBasedPatient = isGrowthPlanUser && !isExistingPatient

  // ========== FIX: Clean URL params - remove treatment_plan_id when order_id exists ==========
  useEffect(() => {
    const params = new URLSearchParams(searchParams)

    // If order_id exists, remove treatment_plan_id from URL
    if (params.has('order_id')) {
      params.delete('treatment_plan_id')
      params.delete('treatmentPlanId')

      // Update URL if changed
      const currentParams = searchParams.toString()
      const newParams = params.toString()
      if (currentParams !== newParams) {
        navigate(`${location.pathname}?${newParams}`, {replace: true})
      }
    }
  }, [orderIdFromUrl, treatmentIdFromUrl, location.pathname, navigate, searchParams])

  // Determine if we should show a specific treatment plan
  const shouldShowSpecificTreatment = useMemo(() => {
    if (orderIdFromUrl && !isLatestOrder) {
      return true
    }
    if (treatmentIdFromUrl && isPastPlan) {
      return true
    }
    if (treatmentIdFromUrl && isEnterprisePlanUser && patientType === 'EXISTING_PATIENT') {
      return true
    }
    return false
  }, [
    orderIdFromUrl,
    isLatestOrder,
    treatmentIdFromUrl,
    isStarterPlanUser,
    isPastPlan,
    isEnterprisePlanUser,
    patientType,
  ])

  // ========== FIXED: Determine if we're in view-only mode ==========
  const isViewOnlyMode = useMemo(() => {
    // For Enterprise or Practice users viewing existing patient's specific treatment plan
    if (patientType === 'EXISTING_PATIENT') {
      if (treatmentIdFromUrl == latestPlanId) return false
    } else {
      return true
    }

    // Past refinement plans are view-only
    if (isPastPlan) {
      return true
    }

    // Previous order view (any user type)
    if (orderIdFromUrl && !isLatestOrder) {
      return true
    }

    return false
  }, [
    isEnterprisePlanUser,
    isPractice,
    patientType,
    treatmentIdFromUrl,
    isStarterPlanUser,
    isPastPlan,
    orderIdFromUrl,
    isLatestOrder,
  ])

  // ========== FIX: Determine effective treatment ID - ignore treatment_id when order_id exists ==========
  const effectiveTreatmentId = useMemo(() => {
    // If order_id exists, treatment_plan_id should be ignored
    if (orderIdFromUrl) {
      return null
    }
    return treatmentIdFromUrl
  }, [orderIdFromUrl, treatmentIdFromUrl])

  const selectedOrderIdForPlans = effectiveTreatmentId
    ? null
    : (currentOrderId ?? (isGrowthOrderBasedPatient ? latestOrderId : null))

  const workflowPlans = useMemo(() => {
    if (!selectedOrderIdForPlans) return []

    return (plansList?.plans_list ?? [])
      .filter(
        (plan: NewPlanData) => String(plan?.order_id ?? '') === String(selectedOrderIdForPlans)
      )
      .map(normalizeWorkflowPlan)
      .filter((plan) => plan.aligner_treatment_id > 0)
  }, [plansList?.plans_list, selectedOrderIdForPlans])

  const filteredPlans = useMemo(() => {
    let plans = allTreatmentPlanList || []

    // Match the plan badge: draft plans store approval on the relevant participant status.
    const isIncludablePlan = (plan: AllTreatmentPlanListItem) => {
      const status =
        plan.treatment_status === treatmentPlanStatusConstants.DRAFT
          ? isPractice || isCustomer
            ? plan.approver_status
            : plan.initiator_status
          : plan.treatment_status

      return (
        plan.treatment_type !== 'BRACES' &&
        (status === treatmentPlanStatusConstants.APPROVED ||
          status === treatmentPlanStatusConstants.ACTIVE ||
          status === treatmentPlanStatusConstants.COMPLETE)
      )
    }

    plans = plans.filter(isIncludablePlan)

    // Order selection takes precedence for both initial and refinement orders.
    if (selectedOrderIdForPlans) {
      const orderPlans = plans.filter(
        (plan: AllTreatmentPlanListItem) =>
          String(plan.order_id) === String(selectedOrderIdForPlans)
      )
      return orderPlans.length ? orderPlans : workflowPlans.filter(isIncludablePlan)
    }

    // If we have effectiveTreatmentId, try to find it in the list
    if (effectiveTreatmentId) {
      const foundPlan = plans.find(
        (plan: AllTreatmentPlanListItem) =>
          String(plan.aligner_treatment_id) === String(effectiveTreatmentId)
      )

      if (foundPlan) {
        return [foundPlan]
      } else {
        return []
      }
    }

    // If we need to show a specific treatment plan by order_id
    if (shouldShowSpecificTreatment) {
      if (orderIdFromUrl) {
        plans = plans.filter(
          (plan: AllTreatmentPlanListItem) => String(plan.order_id) === String(orderIdFromUrl)
        )
      }
      return plans
    }

    return plans
  }, [
    allTreatmentPlanList,
    workflowPlans,
    orderIdFromUrl,
    effectiveTreatmentId,
    shouldShowSpecificTreatment,
    isStarterPlanUser,
    isEnterprisePlanUser,
    selectedOrderIdForPlans,
    patientType,
    isPractice,
    isCustomer,
  ])

  useEffect(() => {
    if (!patientId || !userId) return
    if (!selectedOrderIdForPlans) return

    dispatchAction(
      getNewTreatmentList({
        patient_id: safeParseInt(patientId),
        doctor_id: safeParseInt(userId),
        treatment_subtype: 'ALIGNERS',
        order_id: selectedOrderIdForPlans,
      })
    )
  }, [dispatchAction, patientId, selectedOrderIdForPlans, userId])

  // ========== FIXED: useEffect to fetch treatment plans ==========
  useEffect(() => {
    if (!organizationId || !userId || !patientId) return
    if (getAllTreatmentPlanListLoading) return

    const treatmentIdChanged = currentTreatmentIdRef.current !== treatmentIdFromUrl
    const orderIdChanged = currentOrderIdRef.current !== orderIdFromUrl

    // Update refs
    currentTreatmentIdRef.current = treatmentIdFromUrl
    currentOrderIdRef.current = orderIdFromUrl

    // Create a unique key that includes all relevant parameters
    const currentKey = `${patientId}-${effectiveTreatmentId || 'none'}-${orderIdFromUrl || 'none'}-${isEnterprisePlanUser}-${isStarterPlanUser}-${patientType}`

    // Check if we've already fetched for this exact combination
    if (
      requestedTreatmentPlansForPatientRef.current === currentKey &&
      !treatmentIdChanged &&
      !orderIdChanged
    ) {
      return
    }

    requestedTreatmentPlansForPatientRef.current = currentKey

    // Prepare API payload
    const actionPayload: any = {
      doctor_id: userId,
      patient_id: patientId,
      organization_id: safeParseInt(organizationId),
    }

    // ========== FIX: Use effectiveTreatmentId instead of treatmentIdFromUrl ==========
    // ALWAYS include treatment_plan_id if present in URL (but only if order_id doesn't exist)
    if (effectiveTreatmentId) {
      actionPayload.treatment_plan_id = safeParseInt(effectiveTreatmentId)
    }

    // If order_id is provided, include it in the API call
    if (orderIdFromUrl) {
      actionPayload.order_id = safeParseInt(orderIdFromUrl)
      // ========== FIX: Remove treatment_plan_id from payload if order_id exists ==========
      delete actionPayload.treatment_plan_id
    }

    dispatchAction(getAllTreatmentPlanList(actionPayload))
  }, [
    dispatchAction,
    getAllTreatmentPlanListLoading,
    organizationId,
    patientId,
    userId,
    effectiveTreatmentId,
    isStarterPlanUser,
    isEnterprisePlanUser,
    patientType,
    orderIdFromUrl,
    treatmentIdFromUrl,
  ])

  // Effect to refresh data when URL changes
  useEffect(() => {
    const handlePopState = () => {
      const currentUrlParams = new URLSearchParams(window.location.search)
      const newTreatmentId =
        currentUrlParams.get('treatment_plan_id') ?? currentUrlParams.get('treatmentPlanId')
      const newOrderId = currentUrlParams.get('order_id')

      if (newTreatmentId !== treatmentIdFromUrl || newOrderId !== orderIdFromUrl) {
        requestedTreatmentPlansForPatientRef.current = null
      }
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [treatmentIdFromUrl, orderIdFromUrl])

  // Determine if we should show empty state
  const shouldShowEmptyState = (isPastPlan && !treatmentIdFromUrl) || filteredPlans.length === 0

  // Show loading state if we have treatmentIdFromUrl but no plans yet
  const isLoadingWithTreatmentId =
    effectiveTreatmentId && filteredPlans.length === 0 && getAllTreatmentPlanListLoading

  const plansToRender = useMemo(() => getUniqueTreatmentPlans(filteredPlans), [filteredPlans])

  return (
    <div className='w-full min-h-screen flex flex-col gap-4'>
      <TabHeader
        title='Production'
        description='View and manage all production batches for this case.'
      />

      {getAllTreatmentPlanListLoading && !hasTreatmentPlans ? (
        <div className='flex flex-1 items-center justify-center py-16'>
          <Spinner loading />
        </div>
      ) : isLoadingWithTreatmentId ? (
        <div className='flex flex-1 items-center justify-center py-16'>
          <Spinner loading />
        </div>
      ) : shouldShowEmptyState ? (
        <div className='flex flex-col items-center justify-center py-16'>
          <div className='w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4'>
            <div className='text-3xl text-gray-400'>🗂️</div>
          </div>
          <div className='text-sm text-gray-500 mb-4 text-center max-w-xl'>
            No production batches have been started yet
          </div>
        </div>
      ) : (
        plansToRender.map((plan) => {
          // Ensure we're passing the correct treatment_plan_id
          let selectedPlan = plan
          if (effectiveTreatmentId) {
            const matchingPlan = plansToRender.find(
              (p: AllTreatmentPlanListItem) =>
                String(p.aligner_treatment_id) === String(effectiveTreatmentId)
            )
            if (matchingPlan) {
              selectedPlan = matchingPlan
            }
          }

          return (
            <TreatmentPlanSection
              key={`${getTreatmentPlanIdentity(selectedPlan)}-${effectiveTreatmentId || 'latest'}`}
              plan={selectedPlan}
              setIsDisabled={setIsDisabled}
              isViewOnly={isViewOnlyMode}
            />
          )
        })
      )}
    </div>
  )
}

const Production: React.FC = () => {
  return <StandardProduction />
}

export default Production
